/**
 * PrioritySectionList
 * Groups tasks into collapsible priority sections for the My Tasks page.
 * Sections: Overdue → Pinned → Blocked → Available/In Progress → Completed
 */

import { AlertTriangle, Pin, Lock, Clock, Circle, CheckCircle } from 'lucide-react';
import styled from 'styled-components';
import { CollapsibleSection, EmptyTaskState } from '../../components/tasks';
import { useTaskUIStore } from '../../stores/taskUIStore';
import type { Task, MyTasksSection } from '../../types/tasks';
import { MyTaskRow } from './MyTaskRow';

// ============================================================================
// Types
// ============================================================================

export interface PrioritySectionListProps {
  tasks: Task[];
  onTogglePin?: (taskId: string) => void;
  onViewDetails?: (taskId: string) => void;
}

interface TaskGroups {
  overdue: Task[];
  pinned: Task[];
  blocked: Task[];
  upcoming: Task[];
  available: Task[];
  completed: Task[];
}

// ============================================================================
// Helpers
// ============================================================================

function groupTasksByPriority(tasks: Task[]): TaskGroups {
  return {
    overdue: tasks.filter((t) => t.status === 'OVERDUE'),
    pinned: tasks.filter(
      (t) => t.pinned && !['OVERDUE', 'COMPLETED', 'SKIPPED'].includes(t.status),
    ),
    blocked: tasks.filter((t) => t.status === 'BLOCKED' && !t.pinned),
    // PENDING = scheduled for a future date (not yet actionable). Without this
    // bucket these tasks matched no section and were silently dropped.
    upcoming: tasks.filter((t) => t.status === 'PENDING' && !t.pinned),
    available: tasks.filter(
      (t) => ['AVAILABLE', 'IN_PROGRESS'].includes(t.status) && !t.pinned,
    ),
    completed: tasks.filter((t) => ['COMPLETED', 'SKIPPED'].includes(t.status)),
  };
}

// ============================================================================
// Styled components
// ============================================================================

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

// ============================================================================
// Section config
// ============================================================================

interface SectionDef {
  key: MyTasksSection;
  title: string;
  icon: React.ReactNode;
  variant: 'warning' | 'info' | 'neutral' | 'success';
  tasks: Task[];
}

// ============================================================================
// Component
// ============================================================================

export function PrioritySectionList({ tasks, onTogglePin, onViewDetails }: PrioritySectionListProps) {
  const { expandedSections, toggleSection } = useTaskUIStore();
  const groups = groupTasksByPriority(tasks);

  const sections: SectionDef[] = [
    {
      key: 'overdue',
      title: 'Overdue',
      icon: <AlertTriangle size={15} aria-hidden="true" />,
      variant: 'warning',
      tasks: groups.overdue,
    },
    {
      key: 'pinned',
      title: 'Pinned',
      icon: <Pin size={15} aria-hidden="true" />,
      variant: 'info',
      tasks: groups.pinned,
    },
    {
      key: 'blocked',
      title: 'Blocked',
      icon: <Lock size={15} aria-hidden="true" />,
      variant: 'neutral',
      tasks: groups.blocked,
    },
    {
      key: 'upcoming',
      title: 'Upcoming',
      icon: <Clock size={15} aria-hidden="true" />,
      variant: 'neutral',
      tasks: groups.upcoming,
    },
    {
      key: 'available',
      title: 'Available / In Progress',
      icon: <Circle size={15} aria-hidden="true" />,
      variant: 'info',
      tasks: groups.available,
    },
    {
      key: 'completed',
      title: 'Completed',
      icon: <CheckCircle size={15} aria-hidden="true" />,
      variant: 'success',
      tasks: groups.completed,
    },
  ];

  const visibleSections = sections.filter((s) => s.tasks.length > 0);

  if (visibleSections.length === 0) {
    return <EmptyTaskState message="No tasks match the current filters" />;
  }

  return (
    <ListWrapper>
      {visibleSections.map((section) => (
        <CollapsibleSection
          key={section.key}
          title={section.title}
          count={section.tasks.length}
          icon={section.icon}
          variant={section.variant}
          isExpanded={expandedSections[section.key]}
          onToggle={() => toggleSection(section.key)}
        >
          {section.tasks.map((task) => (
            <MyTaskRow
              key={task.id}
              task={task}
              onTogglePin={onTogglePin}
              onViewDetails={onViewDetails}
            />
          ))}
        </CollapsibleSection>
      ))}
    </ListWrapper>
  );
}

export default PrioritySectionList;
